/**
 * Utility functions for language detection and mapping
 */

export type SupportedLanguage = "en" | "de" | "ar";

/**
 * Maps country codes to supported languages
 * Countries are mapped to their primary language
 */
const COUNTRY_TO_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  // English-speaking countries
  US: "en",
  GB: "en",
  UK: "en",
  CA: "en",
  AU: "en",
  NZ: "en",
  IE: "en",
  ZA: "en",
  IN: "en",
  SG: "en",
  MY: "en",
  PH: "en",
  // German-speaking countries
  DE: "de",
  AT: "de",
  CH: "de",
  LI: "de",
  LU: "de",
  // Arabic-speaking countries
  SA: "ar",
  AE: "ar",
  EG: "ar",
  IQ: "ar",
  JO: "ar",
  KW: "ar",
  LB: "ar",
  LY: "ar",
  MA: "ar",
  OM: "ar",
  QA: "ar",
  SD: "ar",
  SY: "ar",
  TN: "ar",
  YE: "ar",
  BH: "ar",
  DZ: "ar",
  PS: "ar",
};

/**
 * Maps browser language codes to supported languages
 */
const BROWSER_LANGUAGE_MAP: Record<string, SupportedLanguage> = {
  en: "en",
  "en-US": "en",
  "en-GB": "en",
  "en-CA": "en",
  "en-AU": "en",
  de: "de",
  "de-DE": "de",
  "de-AT": "de",
  "de-CH": "de",
  ar: "ar",
  "ar-SA": "ar",
  "ar-AE": "ar",
  "ar-EG": "ar",
};

/**
 * Get language from country code
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @returns Supported language or null if not found
 */
export const getLanguageFromCountry = (countryCode: string | null | undefined): SupportedLanguage | null => {
  if (!countryCode) {
    return null;
  }
  const upperCountryCode = countryCode.toUpperCase();
  return COUNTRY_TO_LANGUAGE_MAP[upperCountryCode] || null;
};

/**
 * Get language from browser language
 * @param browserLang - Browser language code (e.g., "en-US", "de-DE")
 * @returns Supported language or null if not found
 */
export const getLanguageFromBrowser = (browserLang: string | null | undefined): SupportedLanguage | null => {
  if (!browserLang) {
    return null;
  }
  const normalizedLang = browserLang.toLowerCase();

  // Check exact match first
  if (BROWSER_LANGUAGE_MAP[normalizedLang]) {
    return BROWSER_LANGUAGE_MAP[normalizedLang];
  }

  // Check language prefix (e.g., "en" from "en-US")
  const langPrefix = normalizedLang.split("-")[0];
  if (BROWSER_LANGUAGE_MAP[langPrefix]) {
    return BROWSER_LANGUAGE_MAP[langPrefix];
  }

  return null;
};

/**
 * Detect language based on country, browser, or fallback to English
 * Priority: localStorage > country > browser > English
 * @param countryCode - ISO 3166-1 alpha-2 country code
 * @param skipLocalStorage - If true, skip localStorage check (for auto-detection)
 * @returns Supported language (defaults to "en")
 */
export const detectLanguage = (countryCode?: string | null, skipLocalStorage: boolean = false): SupportedLanguage => {
  // First, check localStorage for user preference (unless skipping for auto-detection)
  if (!skipLocalStorage) {
    const savedLanguage = localStorage.getItem("language") as SupportedLanguage | null;
    if (savedLanguage && ["en", "de", "ar"].includes(savedLanguage)) {
      return savedLanguage;
    }
  }

  // Second, try country code
  if (countryCode) {
    const countryLang = getLanguageFromCountry(countryCode);
    if (countryLang) {
      return countryLang;
    }
  }

  // Third, try browser language
  const browserLang = navigator.language || (navigator as any).userLanguage;
  const browserLanguage = getLanguageFromBrowser(browserLang);
  if (browserLanguage) {
    return browserLanguage;
  }

  // Fallback to English
  return "en";
};

